import React, { useEffect, useMemo, useState } from 'react';
import {
  AvDialog,
  AvMessagePopover,
  AvTooltip,
  CustomFrequencyTypesNames,
  FeatureFlags,
  FileFormat,
  OpenSelect,
  SchedulerSetting,
  Select,
  SwitchToggle,
  TextInput,
} from '@fabric-ui/components';
import { ChannelType, DeliveryMethodOptions } from '@fabric-ui/components/Table';
import { Internal, Refresh, X } from '@fabric-ui/icons';
import { InfoFull } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { ellipsis, noop } from '@fabric-ui/utils';
import { autocompleteClasses, Box, Button, Divider, formControlClasses, Popover, Typography } from '@mui/material';
import { configFields as integrationsConfigFields } from '@views/Outegrations/config';
import { useSnackbar } from 'notistack';
import { INPUT_TYPES } from '../../../components/formInputTypes';
import { useAvContext } from '../../../context/AvContextProvider';
import { APP_PATHS, PAGE_PATHS, Permission, PermissionEntitiesNames } from '../../../types';
import { Link } from '../../../utils/AvRouter';
import { formatCronValue, getFileFormat, isValidEmail } from '../../../utils/utils';
import { defaultDeliveryConfig } from '../../CustomDashboards/CustomDashboardsUtils';
import { useEditDashboardSchedule } from '../../CustomDashboards/hooks';
import { ReportsTypes } from '../../CustomDashboards/types';
import { findIntegration, getNewDeliveryConfig, INACTIVE_INTEGRATION_FOR_SCHEDULED_REPORT, integrationsToOptions } from '../reports.utils';
import { ExportDetails } from '../types';
import ActivateSchedulePopup from './ActivateSchedulePopup';
import {
  COMPRESSION_OPTION_GZIP,
  COMPRESSION_OPTION_NONE,
  COMPRESSION_OPTION_ZIP,
  COMPRESSION_OPTION_ZST,
  COMPRESSION_OPTIONS_ARR,
  COMPRESSION_OPTIONS_SPLIT_ARR,
} from './constants';

const resetIcon = <Refresh style={{ transform: 'scaleX(-1)' }} />;
const exitIcon = <X style={{ width: 24, height: 24 }} />;

interface Props {
  initExportDetails: ExportDetails;
  setInitExportDetails: (prev: ExportDetails) => void;
  anchorEl?: Element;
  setAnchorEl?: React.Dispatch<React.SetStateAction<Element | undefined>>;
  formatOptions?: string[];
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  helperTexts?: { title: string; message: string };
  dashboardReportId: string;
  allowEdit?: boolean;
  isCompressAvailable?: boolean;
  availableIntegrationOptions;
  type: ReportsTypes;
  warningMessage?: string;
  scheduledBy?;
}
export const ScheduleExportWindow: React.FC<Props> = ({
  initExportDetails,
  setInitExportDetails,
  anchorEl,
  setAnchorEl = noop,
  isOpen = false,
  formatOptions = Object.keys(FileFormat),
  setIsOpen = noop,
  helperTexts = {
    title: 'Avalor - {account_name} | New {report_name} report available',
    message: `Your scheduled report is ready.\nReport Name: {report_name} : {report_id}`,
  },
  dashboardReportId,
  allowEdit = true,
  isCompressAvailable = false,
  availableIntegrationOptions,
  type,
  warningMessage = '',
  scheduledBy,
}) => {
  const {
    user,
    getPathName,
    userPermissions: { hasAllowedPermissionToResource },
    featureFlags,
  } = useAvContext();
  const hasPermissionsToIntegrations = hasAllowedPermissionToResource({
    resource: PermissionEntitiesNames.INTEGRATION,
    permission: Permission.READ,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [exportDetails, setExportDetails] = useState(initExportDetails);
  const [isActivateSchedulePopupOpen, setIsActivateSchedulePopupOpen] = useState(false);

  const { mutate: save } = useEditDashboardSchedule(() => {
    setInitExportDetails(exportDetails);
    setIsOpen(false);
    setAnchorEl(undefined);
  });

  const deliveryMethodType = exportDetails.deliveryConfig?.type || DeliveryMethodOptions.Email;
  const title = exportDetails.deliveryConfig?.title || '';
  const recipients = exportDetails.deliveryConfig?.recipients || [];
  const deliveryMethod = exportDetails.deliveryConfig?.integrationId || null;

  const onChangeExportDetails = key => value => setExportDetails(prev => ({ ...prev, [key]: value }));
  const cron = exportDetails.intervalExpression ? `0 ${exportDetails.intervalExpression}` : exportDetails.intervalExpression;
  const setCron = field => val => onChangeExportDetails(field)(formatCronValue(val) || null);

  const selectedIntegrationInfo = useMemo(
    () => findIntegration(availableIntegrationOptions, exportDetails),
    [availableIntegrationOptions, exportDetails.deliveryConfig?.integrationId]
  );

  const onChangeConfig = key => value =>
    setExportDetails(prev => {
      const baseConfig = prev.deliveryConfig ?? defaultDeliveryConfig(getFileFormat(type));
      if (key === 'splitExportFiles' && value === true) {
        const currentCompressType = 'compressType' in baseConfig ? baseConfig.compressType : undefined;
        const nextCompressType =
          currentCompressType === COMPRESSION_OPTION_GZIP.value || currentCompressType === COMPRESSION_OPTION_ZST.value
            ? currentCompressType
            : COMPRESSION_OPTION_ZST.value;
        return {
          ...prev,
          deliveryConfig: {
            ...baseConfig,
            splitExportFiles: true,
            compressFile: true,
            compressType: nextCompressType,
          },
        };
      }
      return {
        ...prev,
        deliveryConfig: {
          ...baseConfig,
          [key]: value,
        },
      };
    });

  const onChangeCompression = (
    value:
      | typeof COMPRESSION_OPTION_NONE.value
      | typeof COMPRESSION_OPTION_ZIP.value
      | typeof COMPRESSION_OPTION_ZST.value
      | typeof COMPRESSION_OPTION_GZIP.value
  ) => {
    const compressFile = value !== COMPRESSION_OPTION_NONE.value;
    setExportDetails(prev => ({
      ...prev,
      deliveryConfig: { ...(prev.deliveryConfig ?? defaultDeliveryConfig(getFileFormat(type))), compressFile, compressType: value },
    }));
  };

  const onChangeFormat = newFormat => {
    setExportDetails(prev => ({
      ...prev,
      deliveryConfig: prev.deliveryConfig
        ? {
            ...prev.deliveryConfig,
            attachmentMetadata: {
              fileFormat: newFormat,
            },
          }
        : undefined,
    }));
  };

  const onReset = () => setExportDetails(title === '' ? { ...initExportDetails } : initExportDetails);
  const onClose = () => setIsOpen(false);
  const handleClose = () => {
    onClose();
    onReset();
  };

  const handleSave = () => {
    if (allowEdit) {
      save({
        id: dashboardReportId,
        active: exportDetails.enabled,
        intervalExpression: exportDetails.intervalExpression,
        deliveryConfig: getNewDeliveryConfig(exportDetails.deliveryConfig),
      });
    }
  };

  useEffect(() => setExportDetails(title === '' ? { ...initExportDetails } : initExportDetails), [initExportDetails]);

  const isSplitEnabled = !!exportDetails.deliveryConfig?.splitExportFiles;

  const compressTypeValue = exportDetails.deliveryConfig?.compressFile
    ? exportDetails.deliveryConfig?.compressType || COMPRESSION_OPTION_ZST.value
    : COMPRESSION_OPTION_NONE.value;

  const scheduledByName = useMemo(() => {
    if (scheduledBy) {
      return `${scheduledBy.firstName} ${scheduledBy.lastName}`;
    }
    return undefined;
  }, [scheduledBy]);

  const exportFormatSelect = (
    <>
      {featureFlags[FeatureFlags.ScheduleExportPermissions] && scheduledBy ? (
        <>
          <Box sx={{ ...flex.justifyBetween, gap: 2 }}>
            <TextInput size="small" label="Scheduled By" disabled value={scheduledByName} style={{ width: 340, maxWidth: 340 }} />
            <SwitchToggle
              sx={{ marginTop: 3.25 }}
              label="Active"
              value={exportDetails.enabled}
              onChange={() => {
                const willBeEnabled = !exportDetails.enabled;
                if (willBeEnabled && scheduledBy && scheduledBy.id !== user.userId) {
                  setIsActivateSchedulePopupOpen(true);
                } else {
                  onChangeExportDetails('enabled')(willBeEnabled);
                }
              }}
              labelPlacement="start"
            />
          </Box>
          <Select
            muiProps={{ disableClearable: true }}
            size="small"
            label="Export Format"
            style={{
              width: 340,
              [`&.${autocompleteClasses.root}, .${formControlClasses.root}`]: {
                maxWidth: 340,
              },
            }}
            value={exportDetails.deliveryConfig?.attachmentMetadata?.fileFormat}
            onChange={onChangeFormat}
            options={formatOptions as any}
            getLabelFunc={(v: any) => (v === FileFormat.JSON_FORMAT ? 'JSONL' : v.replace('_FORMAT', ''))}
            getValueFunc={(v: any) => v}
          />
          <ActivateSchedulePopup
            isOpen={isActivateSchedulePopupOpen}
            onClose={() => setIsActivateSchedulePopupOpen(false)}
            onApprove={() => {
              onChangeExportDetails('enabled')(true);
              setIsActivateSchedulePopupOpen(false);
            }}
          />
        </>
      ) : (
        <Box sx={{ ...flex.justifyBetween, gap: 2 }}>
          <Select
            muiProps={{ disableClearable: true }}
            size="small"
            label="Export Format"
            style={{
              width: 340,
              [`&.${autocompleteClasses.root}, .${formControlClasses.root}`]: {
                maxWidth: 340,
              },
            }}
            value={exportDetails.deliveryConfig?.attachmentMetadata?.fileFormat}
            onChange={onChangeFormat}
            options={formatOptions as any}
            getLabelFunc={(v: any) => (v === FileFormat.JSON_FORMAT ? 'JSONL' : v.replace('_FORMAT', ''))}
            getValueFunc={(v: any) => v}
          />
          <SwitchToggle
            sx={{ marginTop: 3.25 }}
            label="Active"
            value={exportDetails.enabled}
            onChange={() => onChangeExportDetails('enabled')(!exportDetails.enabled)}
            labelPlacement="start"
          />
        </Box>
      )}

      {isCompressAvailable && (
        <Box sx={{ ...flex.col, gap: 0.5 }}>
          <Select
            value={compressTypeValue}
            onChange={onChangeCompression}
            options={isSplitEnabled ? COMPRESSION_OPTIONS_SPLIT_ARR : COMPRESSION_OPTIONS_ARR}
            label="Compression"
            size="small"
            style={{
              width: 340,
              [`&.${autocompleteClasses.root}, .${formControlClasses.root}`]: {
                maxWidth: 340,
              },
            }}
          />
          {isSplitEnabled && (
            <Typography variant="caption" color="text.secondary">
              File splitting only supports ZST and GZIP compression.
            </Typography>
          )}
        </Box>
      )}
    </>
  );

  const readOnlyStyle: any = allowEdit
    ? {}
    : { pointerEvents: 'none', '.MuiInputBase-root': { background: 'var(--color-surface-field-disabled)' } };

  const subjectInputs = {
    label: 'Title',
    key: 'title',
    isRequired: false,
    helperText: helperTexts.title,
    sx: { maxWidth: 'unset' },
  };
  const bodyInputs = {
    label: 'Message',
    key: 'message',
    isRequired: false,
    helperText: helperTexts.message,
    multiline: true,
    rows: 7,
    sx: {
      width: 'unset',
      maxWidth: 'unset',
      '&::placeholder': {
        ...ellipsis,
      },
    },
  };
  const getByType = {
    [DeliveryMethodOptions.Email]: [subjectInputs, bodyInputs],
    [DeliveryMethodOptions.S3]: [
      {
        key: 'path',
        label: (
          <Box sx={{ ...flex.itemsCenter, gap: 0.5 }}>
            S3 Path
            <AvMessagePopover
              message="This path is inherited from the AWS S3 Outegration and can be overridden for this report."
              icon={<InfoFull />}
              asTooltip
            />
          </Box>
        ),
        placeholder: 'reports/custom-folder (optional)',
      },
      ...(featureFlags[FeatureFlags.SplitExportFiles]
        ? [
            {
              key: 'splitExportFiles',
              label: (
                <Box sx={{ ...flex.itemsCenter, gap: 0.5 }}>
                  Enable file splitting
                  <AvMessagePopover
                    message="Splits the export into several files when the size is too large; Data sorting is not preserved."
                    icon={<InfoFull />}
                    asTooltip
                  />
                </Box>
              ),
              type: INPUT_TYPES.BOOL,
            },
          ]
        : []),
    ],
    [DeliveryMethodOptions.GCP_STORAGE]: [
      {
        key: 'path',
        label: (
          <Box sx={{ ...flex.itemsCenter, gap: 0.5 }}>
            GCS Path
            <AvMessagePopover
              message="This path is inherited from the GCP Storage Outegration and can be overridden for this report."
              icon={<InfoFull />}
              asTooltip
            />
          </Box>
        ),
        placeholder: 'reports/custom-folder (optional)',
      },
    ],
    [DeliveryMethodOptions.SLACK]: [
      subjectInputs,
      {
        label: 'Channel Type',
        key: 'channelType',
        type: INPUT_TYPES.RADIO,
        options: [
          { title: 'Private', value: ChannelType.PRIVATE_CHANNEL },
          { title: 'Public', value: ChannelType.PUBLIC_CHANNEL },
        ],
      },
      {
        label: 'Channel',
        key: 'channel',
        isRequired: false,
        sx: { width: '385px', maxWidth: 'unset' },
      },
      bodyInputs,
    ],
    [DeliveryMethodOptions.TEAMS]: [
      subjectInputs,
      {
        label: 'Channel Id',
        key: 'channelId',
        isRequired: false,
        sx: { width: '385px', maxWidth: 'unset' },
      },
      bodyInputs,
    ],
  };

  const inputsByType = type =>
    getByType[type].map(({ type: Component = TextInput, key, label, ...rest }) => (
      <Component
        key={key}
        label={label}
        value={exportDetails.deliveryConfig?.[key]}
        onChange={v => onChangeConfig(key)(v)}
        size="small"
        style={{ width: 'unset' }}
        {...rest}
      />
    ));

  const isSaveDisabled =
    (exportDetails.enabled && deliveryMethodType === DeliveryMethodOptions.Email && !recipients?.length) ||
    selectedIntegrationInfo?.active === false ||
    !allowEdit;

  const commonParts = (
    <>
      <Box sx={{ ...flex.col, gap: 2 }}>{exportFormatSelect}</Box>
      <Divider />
      <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
        <Select
          muiProps={{ disableClearable: true }}
          size="small"
          label="Delivery Method"
          style={{
            width: 340,
            [`&.${autocompleteClasses.root}, .${formControlClasses.root}`]: {
              maxWidth: 340,
            },
          }}
          value={deliveryMethod}
          onChange={v => {
            const integration = availableIntegrationOptions.find(({ id }) => id === v);
            if (integration) {
              setExportDetails(prev => ({
                ...prev,
                deliveryConfig: getNewDeliveryConfig({
                  ...prev.deliveryConfig,
                  type: integration.integrationType,
                  integrationId: v,
                  channelType: integration?.redactedConfig?.channelType,
                  channelId: integration?.redactedConfig?.channelId,
                  channel: integration?.redactedConfig?.channel,
                  path: integration?.redactedConfig?.path,
                }),
              }));
            }
          }}
          options={availableIntegrationOptions.map(integrationsToOptions)}
          error={selectedIntegrationInfo?.active === false && exportDetails.enabled ? INACTIVE_INTEGRATION_FOR_SCHEDULED_REPORT : undefined}
        />
        {hasPermissionsToIntegrations &&
          deliveryMethodType !== DeliveryMethodOptions.Email &&
          exportDetails.deliveryConfig?.integrationId && (
            <AvTooltip title="Open in new Tab">
              <Button
                sx={{ mt: '22px' }}
                component={Link}
                target="_blank"
                to={
                  getPathName(`${PAGE_PATHS.TARGETS}/edit/${exportDetails.deliveryConfig?.integrationId}`, undefined, APP_PATHS.PLATFORM).to
                }
                disabled={!allowEdit}>
                <Internal />
              </Button>
            </AvTooltip>
          )}
      </Box>
      {deliveryMethodType === DeliveryMethodOptions.Email ? (
        <OpenSelect
          isRequired
          size="small"
          label="Recipient"
          style={{ width: 340 }}
          value={recipients}
          onChange={v => {
            const invalidEmail = v.find(val => !isValidEmail(val));
            if (invalidEmail) {
              enqueueSnackbar(`${invalidEmail} is not a valid email address`, { variant: 'error' });
            } else {
              if (recipients?.length === 0) {
                onChangeExportDetails('enabled')(true);
              }
              onChangeConfig('recipients')(v);
            }
          }}
          isMultiple
        />
      ) : (
        deliveryMethodType &&
        hasPermissionsToIntegrations &&
        selectedIntegrationInfo &&
        integrationsConfigFields[deliveryMethodType]?.fields?.map(
          ({ label, valueName, type: Component = TextInput, ...rest }: Record<string, any>) =>
            (!rest.shouldShow || rest.shouldShow(integrationsConfigFields[deliveryMethodType])) &&
            getByType[deliveryMethodType]?.every(v => v.key !== valueName) ? (
              <Component
                {...rest.params}
                size="small"
                key={label}
                label={label}
                disabled
                value={selectedIntegrationInfo?.redactedConfig?.[valueName]}
                style={{
                  width: 340,
                  maxWidth: 340,
                  [`&.${autocompleteClasses.root}, .${formControlClasses.root}`]: {
                    maxWidth: 340,
                  },
                }}
              />
            ) : null
        )
      )}
    </>
  );

  const renderDialog = (
    <AvDialog
      open={isOpen}
      onClose={handleClose}
      title="Schedule Export Details"
      footer={
        <Box sx={{ ...flex.row, ...readOnlyStyle }}>
          <Button size="small" onClick={onReset} variant="outlined" disabled={!allowEdit}>
            {resetIcon} Reset
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ ...flex.justifyEndCenter, gap: 2.5 }}>
            <Button onClick={handleClose} disabled={!allowEdit}>
              Cancel
            </Button>
            <AvTooltip title={warningMessage}>
              <Button variant="contained" onClick={handleSave} disabled={isSaveDisabled}>
                Save
              </Button>
            </AvTooltip>
          </Box>
        </Box>
      }>
      <Box sx={{ ...flex.col, gap: 2, ...readOnlyStyle }}>
        {commonParts}
        <SchedulerSetting
          cron={cron}
          setCron={setCron('intervalExpression')}
          customTimeTypes={[CustomFrequencyTypesNames.DAYS, CustomFrequencyTypesNames.HOURS]}
          sx={{ ...flex.wrap, justifyContent: 'start', flexDirection: 'row' }}
        />
        {!!getByType[deliveryMethodType]?.length && <Divider />}
        {inputsByType(deliveryMethodType)}
      </Box>
    </AvDialog>
  );

  const renderPopover = (
    <Popover
      onClick={e => e.stopPropagation()}
      open={Boolean(isOpen)}
      anchorEl={anchorEl}
      transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
      onClose={onClose}>
      <Box sx={{ ...flex.col, gap: 2, p: 3, width: 600, ...readOnlyStyle }}>
        <Box sx={{ ...flex.justifyBetweenCenter, py: 1.5, pl: -1 }}>
          <Typography variant="h5">Schedule Export Details</Typography>
          <Button onClick={onClose}>{exitIcon}</Button>
        </Box>
        <div>
          {commonParts}
          <SchedulerSetting
            cron={cron}
            setCron={setCron('intervalExpression')}
            customTimeTypes={[CustomFrequencyTypesNames.DAYS, CustomFrequencyTypesNames.HOURS]}
            sx={{ ...flex.row, flexDirection: 'row', justifyContent: 'start', flexWrap: 'wrap' }}
          />
        </div>
        <Divider />
        {inputsByType(deliveryMethodType)}
        <Divider />
        <Box sx={{ ...flex.row, ...readOnlyStyle }}>
          <Button size="small" onClick={onReset} variant="outlined" disabled={!allowEdit}>
            {resetIcon} Reset
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ ...flex.justifyEndCenter, gap: 2.5 }}>
            <Button onClick={handleClose} disabled={!allowEdit}>
              Cancel
            </Button>
            <AvTooltip title={warningMessage}>
              <Button variant="contained" onClick={handleSave} disabled={isSaveDisabled}>
                Save Schedule
              </Button>
            </AvTooltip>
          </Box>
        </Box>
      </Box>
    </Popover>
  );

  return anchorEl ? renderPopover : renderDialog;
};
